import apiClient from './apiClient';

// Types for dashboard data
export interface TodayAppointment {
  id: string;
  time: string;
  patientName: string;
  clientName: string;
  veterinarian: string;
  service: string;
  status: 'scheduled' | 'confirmed' | 'present' | 'no-show' | 'cancelled';
  noShowRisk: 'low' | 'medium' | 'high';
  isUrgent: boolean;
}

export interface RevenueData {
  today: number;
  thisMonth: number;
  outstanding: number;
  topServices: Array<{
    name: string;
    revenue: number;
    count: number;
  }>;
}

export interface StockAlert {
  id: string;
  itemName: string;
  currentStock: number;
  minStock: number;
  type: 'low_stock' | 'expiring_soon' | 'expired';
  daysToExpiry?: number;
  severity: 'low' | 'medium' | 'high';
}

export interface VaccinationDue {
  id: string;
  patientName: string;
  clientName: string;
  vaccinationType: string;
  dueDate: string;
  daysOverdue: number;
  isOverdue: boolean;
}

export interface NPSFeedback {
  id: string;
  clientName: string;
  rating: number;
  comment: string;
  date: string;
  service: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  assignedTo: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  category: 'reception' | 'medical' | 'admin';
  status: 'pending' | 'in_progress' | 'completed';
}

export interface DashboardFilters {
  location?: string;
  veterinarian?: string;
  dateRange?: 'today' | 'tomorrow' | 'week' | 'month';
  clinicId?: string;
}

class DashboardService {
  async getTodayAppointments(filters: DashboardFilters = {}): Promise<TodayAppointment[]> {
    try {
      const params = new URLSearchParams();
      if (filters.location && filters.location !== 'all') {
        params.append('location', filters.location);
      }
      if (filters.veterinarian && filters.veterinarian !== 'all') {
        params.append('veterinarian', filters.veterinarian);
      }
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/appointments/today?${params}`);

      const appointments = response.data?.data || [];
      
      // Transformam datele backend in format frontend
      return appointments.map((app: any) => ({
        id: app.id,
        time: new Date(app.scheduledAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }),
        patientName: app.animalName || 'N/A',
        clientName: `${app.ownerFirstName || ''} ${app.ownerLastName || ''}`.trim() || 'N/A',
        veterinarian: `${app.vetFirstName || ''} ${app.vetLastName || ''}`.trim() || 'Neasignat',
        service: app.reason || 'Consultatie',
        status: app.status || 'scheduled',
        noShowRisk: 'low',
        isUrgent: app.emergency || false
      }));
    } catch (error) {
      console.error('Error fetching today appointments:', error);
      return [];
    }
  }

  async getRevenueData(filters: DashboardFilters = {}): Promise<RevenueData> {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) {
        params.append('dateRange', filters.dateRange);
      }
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/revenue?${params}`);

      // Backend returneaza { success: true, data: { total, count, dateRange } }
      const backendData = response.data?.data || {};
      
      return {
        today: backendData?.total || 0,
        thisMonth: backendData?.total || 0, // Pentru moment, folosim acelasi total
        outstanding: 0, // Nu avem aceasta informatie inca
        topServices: [] // Nu avem aceasta informatie inca
      };
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      return {
        today: 0,
        thisMonth: 0,
        outstanding: 0,
        topServices: []
      };
    }
  }

  async getStockAlerts(filters: DashboardFilters = {}): Promise<StockAlert[]> {
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/stock-alerts?${params}`);

      const alerts = response.data?.data || [];
      
      // Transformam datele backend in format frontend
      return alerts.map((alert: any) => ({
        id: alert.id,
        itemName: alert.productName || 'Produs necunoscut',
        currentStock: alert.currentStock || 0,
        minStock: alert.minStock || 0,
        type: alert.status === 'critical' ? 'low_stock' : 'low_stock',
        severity: alert.status === 'critical' ? 'high' : alert.status === 'low' ? 'medium' : 'low'
      }));
    } catch (error) {
      console.error('Error fetching stock alerts:', error);
      return [];
    }
  }

  async getVaccinationsDue(filters: DashboardFilters = {}): Promise<VaccinationDue[]> {
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/vaccinations-due?${params}`);

      const vaccinations = response.data?.data || [];
      
      // Transformam datele backend in format frontend
      return vaccinations.map((vacc: any) => ({
        id: vacc.id,
        patientName: vacc.animalName || 'N/A',
        clientName: vacc.ownerName || 'N/A',
        vaccinationType: vacc.vaccineType || 'N/A',
        dueDate: vacc.dueDate || new Date().toISOString(),
        daysOverdue: 0,
        isOverdue: new Date(vacc.dueDate) < new Date()
      }));
    } catch (error) {
      console.error('Error fetching vaccinations due:', error);
      return [];
    }
  }

  async getNPSFeedback(filters: DashboardFilters = {}): Promise<NPSFeedback[]> {
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/nps-feedback?${params}`);

      const feedback = response.data?.data?.recentFeedback || [];
      
      // Transformam datele backend in format frontend
      return feedback.map((fb: any) => ({
        id: fb.id,
        clientName: 'Client',
        rating: fb.score || 0,
        comment: fb.comment || '',
        date: fb.date || new Date().toISOString(),
        service: 'Consultatie'
      }));
    } catch (error) {
      console.error('Error fetching NPS feedback:', error);
      return [];
    }
  }

  async getTasks(filters: DashboardFilters = {}): Promise<Task[]> {
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }

      const response = await apiClient.get(`/dashboard/tasks?${params}`);

      const tasks = response.data?.data || [];
      
      // Transformam datele backend in format frontend
      return tasks.map((task: any) => ({
        id: task.id,
        title: task.title || '',
        description: task.description || '',
        assignedTo: task.assignedTo || 'Neasignat',
        dueDate: task.dueDate || new Date().toISOString(),
        priority: task.priority || 'low',
        category: 'admin',
        status: task.status || 'pending'
      }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  }

  async getAllDashboardData(filters: DashboardFilters = {}): Promise<{
    appointments: TodayAppointment[];
    revenue: RevenueData;
    stockAlerts: StockAlert[];
    vaccinations: VaccinationDue[];
    feedback: NPSFeedback[];
    tasks: Task[];
  }> {
    try {
      const [appointments, revenue, stockAlerts, vaccinations, feedback, tasks] = await Promise.all([
        this.getTodayAppointments(filters),
        this.getRevenueData(filters),
        this.getStockAlerts(filters),
        this.getVaccinationsDue(filters),
        this.getNPSFeedback(filters),
        this.getTasks(filters)
      ]);

      return {
        appointments,
        revenue,
        stockAlerts,
        vaccinations,
        feedback,
        tasks
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return {
        appointments: [],
        revenue: {
          today: 0,
          thisMonth: 0,
          outstanding: 0,
          topServices: []
        },
        stockAlerts: [],
        vaccinations: [],
        feedback: [],
        tasks: []
      };
    }
  }
}

export const dashboardService = new DashboardService();
